import { definedMap, withDefault } from "collection-utils";
import first from "lodash/first";
import type { ArrayScreenHandler } from ".";
import type {
    SerializedComponent,
    SerializedComponentsObject,
    SerializedMenuItem,
} from "../../common/components/SerializedApp";
import { ScreenFlag } from "../../common/components/SerializedApp";
import { MenuItemPurpose } from "../../common/components/types";
import { getFeatureSetting } from "../../common/feature-settings";
import { rowIndexColumnName } from "../../common/google-sheets";
import type { IconName } from "../../common/named-icons";
import { makeSingletonMap, mapFilterUndefined, truthify } from "../../common/Support";
import { handlerForActionKind } from "../actions";
import type { PivotContent, ScreenWithMenuItemsContent } from "../builder-utils";
import {
    compileFilters,
    convertForSort,
    makeErrorScreen,
    makeFilteredArray,
    makeQuotaBannerMessage,
    makeSearchBar,
    makeSearchedArray,
    makeSearchVariables,
    screenErrorMissingTable,
} from "../builder-utils";
import type { AppDescriptionContext, ComponentBuilder, PropertyDescriptor } from "../components";
import { ArrayPropertyStyle } from "../components";
import { getActionsForArrayContent } from "../components/component-utils";
import { PopulationMode } from "../components/description-handlers";
import { dynamicFilterColumnPropertyHandler } from "../components/descriptor-utils";
import { populateDescription } from "../components/populate-description";
import { StringPropertyHandler, SwitchPropertyHandler } from "../components/property-handlers";
import { PropertySection } from "../components/property-sections";
import {
    getArrayProperty,
    getColumnProperty,
    getSwitchProperty,
    makeStringProperty,
    makeSwitchProperty,
} from "../components/property-utils";
import type {
    ActionDescription,
    ArrayContentDescription,
    ArrayScreenDescription,
    ComponentDescription,
    DynamicSortColumn,
    InputOutputTables,
    TableColumn,
    TableGlideType,
} from "../../common/description";
import {
    ActionKind,
    ArrayScreenFormat,
    ArrayTransformKind,
    filterOrderingArrayTransforms,
    findTable,
    getPrimitiveNonHiddenColumns,
    getTableColumn,
    getTableColumnDisplayName,
    getTableName,
    isSingleRelationType,
    makeInputOutputTables,
    makeTableRef,
    PropertyKind,
    ScreenDescriptionKind,
    SortOrder,
} from "../../common/description";
import { withColumnFormat } from "../formulas";
import { decomposeSortKey } from "../formulas/formula-specifications";
import type { LetBinding, SerializedElementBuilder } from "../serialized-component";
import { ArrayContentHandlerBase } from "./array-content";

// ##arrayScreenSearch: These are the two property handlers for search in
// array screens, but we're duplicating their functionality in the faux search
// component.
export const searchPropertyHandler = new SwitchPropertyHandler(
    { search: true },
    "Show search bar",
    PropertySection.Search
);

export const searchPlaceholderPropertyName = "searchPlaceholder";

export const searchPlaceholderPropertyLabel = "Placeholder";
export const searchPlaceholderPropertyPlaceholder = `e.g. Search People`;
export const searchPlaceholderPropertyHandler = new StringPropertyHandler(
    searchPlaceholderPropertyName,
    searchPlaceholderPropertyLabel,
    searchPlaceholderPropertyPlaceholder,
    false,
    undefined,
    PropertySection.Search
);

export const sortPropertyVariableName = "sortProperty";
export const sortPropertyPropertyName = "property";

const dynamicSortColumnsPropertyHandler: PropertyDescriptor = {
    kind: PropertyKind.Array,
    label: "In-app sort",
    property: { name: "dynamicSortColumns" },
    section: PropertySection.InAppSortData,
    properties: [
        {
            kind: PropertyKind.Column,
            property: { name: "column" },
            section: PropertySection.InAppSortData,
            label: "Sort by",
            required: true,
            editable: true,
            searchable: false,
            emptyByDefault: false,
            getIndirectTable: undefined,
            getAllowedColumns: getPrimitiveNonHiddenColumns,
        },
    ],
    allowEmpty: true,
    allowReorder: true,
    addItemLabels: ["Add sort"],
    style: ArrayPropertyStyle.Default,
};

interface MakeSearchResult {
    readonly contextLets: readonly LetBinding[];
    readonly arrayData: SerializedComponent;
    readonly originalArray: SerializedComponent;
    readonly topBar?: SerializedComponent;
    readonly searchActiveFlag: SerializedComponent;
}

const ascIconName = "01-40-keyboard-arrow-down";
const descIconName = "01-40-keyboard-arrow-up";
const shuffleIconName = "01-42-button-shuffle";

function getCanAddRow(desc: ArrayScreenDescription | undefined): boolean {
    return truthify(getSwitchProperty(desc?.canAddRow));
}

export abstract class ArrayScreenHandlerBase<
        TContentDesc extends ArrayContentDescription,
        TScreenDesc extends ArrayScreenDescription & TContentDesc
    >
    extends ArrayContentHandlerBase<TContentDesc>
    implements ArrayScreenHandler<TContentDesc, TScreenDesc>
{
    constructor(format: ArrayScreenFormat, public readonly label: string, public readonly icon: IconName) {
        super(format);
    }

    public get isLegacy(): boolean {
        return false;
    }

    public get supportsInlineList(): boolean {
        return true;
    }

    protected abstract makeDetailScreenTitle(
        builder: ComponentBuilder,
        desc: TContentDesc,
        table: TableGlideType,
        ctx: SerializedComponent
    ): SerializedComponent | undefined;

    protected getItemID(
        b: SerializedElementBuilder,
        ctx: SerializedComponent,
        table: TableGlideType
    ): SerializedComponent | undefined {
        return b.getProperty(rowIndexColumnName, ctx, getTableName(table));
    }

    protected makeActions(
        builder: ComponentBuilder,
        desc: TContentDesc,
        actions: readonly ActionDescription[],
        table: TableGlideType
    ): SerializedComponent | undefined {
        const { b } = builder;
        const builtActions = mapFilterUndefined(actions, a => {
            const handler = handlerForActionKind(a.kind);
            if (handler === undefined) return undefined;
            return handler.makeAction(
                builder,
                a,
                makeInputOutputTables(table),
                b.getContext,
                b.getContext,
                undefined,
                this.makeDetailScreenTitle(builder, desc, table, b.getContext),
                this.getItemID(builder.b, b.getContext, table),
                undefined,
                true
            );
        });
        if (builtActions.length === 0) return undefined;
        return b.sequence(builtActions);
    }

    protected makeDefaultAction(
        builder: ComponentBuilder,
        desc: TContentDesc,
        table: TableGlideType
    ): SerializedComponent | undefined {
        return this.makeActions(
            builder,
            desc,
            getActionsForArrayContent<TContentDesc>(this, desc, builder).actions,
            table
        );
    }

    public makeScreenContent(
        builder: ComponentBuilder,
        screenName: string,
        desc: TScreenDesc,
        incomingArrayData: SerializedComponent,
        title: SerializedComponent,
        arrayScreenRow: SerializedComponent,
        uniqueID: string
    ): ScreenWithMenuItemsContent {
        const { b, schema } = builder;
        const { type: columnType, pivots } = desc;
        const search = this.makeSearch(b, builder, desc, incomingArrayData, title);
        const { originalArray, topBar, searchActiveFlag } = search;
        let { contextLets, arrayData } = search;

        const menuItems: SerializedMenuItem[] = [];
        if (getCanAddRow(desc)) {
            const onTap = builder.makePushAddItemScreen(columnType, originalArray);
            if (onTap !== undefined) {
                const isVisible = compileFilters(builder, undefined, b.constant({}), desc.canAddRowFilters ?? []);
                menuItems.push({
                    title: b.localizedString("add"),
                    icon: b.icon("00-01-glide-add"),
                    onTap,
                    purpose: b.constant(MenuItemPurpose.AddRow),
                    isVisible,
                });
            }
        }

        const dynamicFilterColumnName = dynamicFilterColumnPropertyHandler.getColumnName(desc);

        const table = isSingleRelationType(columnType) ? findTable(schema, columnType) : undefined;
        if (table === undefined) {
            return makeErrorScreen(b, screenName, screenErrorMissingTable);
        }

        const dynamicFilterColumn =
            dynamicFilterColumnName !== undefined ? getTableColumn(table, dynamicFilterColumnName) : undefined;

        const withSort = this.getNeedsOrder(desc);
        let sortColumns: TableColumn[];
        if (withSort) {
            sortColumns = Array.from(
                new Set(
                    (getArrayProperty<DynamicSortColumn>(desc.dynamicSortColumns)
                        ?.map(dsc => definedMap(getColumnProperty(dsc.column), c => getTableColumn(table, c)))
                        ?.filter(c => c !== undefined) as TableColumn[]) ?? []
                )
            );
        } else {
            sortColumns = [];
        }

        const processedArray = this.processArray(
            builder,
            desc,
            table,
            arrayData,
            undefined,
            uniqueID,
            withSort && sortColumns.length <= 0
        );
        contextLets = [...contextLets, ["processed", processedArray]];
        arrayData = b.getVariable("processed");

        if (dynamicFilterColumn !== undefined || sortColumns.length > 0) {
            let filterItems: SerializedComponent | undefined;
            let filterValues: SerializedComponent | undefined;

            if (dynamicFilterColumn !== undefined) {
                contextLets = [...contextLets, ["dynamicFilterValues", b.data([])]];
                const filterPrimitives = b.sort(
                    b.uniqueAndMapValues(arrayData, b.getProperty(dynamicFilterColumn.name, b.getContext, table)),
                    b.getContext,
                    b.constant("asc")
                );
                filterItems = b.map(
                    filterPrimitives,
                    b.object({
                        name: withColumnFormat(builder, b.getContext, table, dynamicFilterColumn),
                        value: b.getContext,
                    })
                );
                filterValues = b.getVariable("dynamicFilterValues");

                let filterCondition: SerializedComponent;
                if (dynamicFilterColumn.type.kind === "array") {
                    filterCondition = b.arraysOverlap(
                        filterValues,
                        b.getProperty(dynamicFilterColumn.name, b.getContext, table)
                    );
                } else {
                    filterCondition = b.arrayContains(
                        filterValues,
                        b.getProperty(dynamicFilterColumn.name, b.getContext, table)
                    );
                }
                contextLets = [
                    ...contextLets,
                    [
                        "dynamicallyFiltered",
                        b.filter(arrayData, b.ifIsNonEmptyArray(filterValues, filterCondition, b.constant(true))),
                    ],
                ];
                arrayData = b.getVariable("dynamicallyFiltered");
            }

            let sortProperties: SerializedComponent | undefined;
            let sortProperty: SerializedComponent | undefined;
            let sortAscending: SerializedComponent | undefined;

            if (sortColumns.length > 0) {
                const sortTransform = first(filterOrderingArrayTransforms(desc.transforms ?? []));
                let allSortColumns: TableColumn[];
                let additionalProperty: SerializedComponent | undefined;
                let defaultProperty: string | boolean;
                let ascendingByDefault: boolean;

                if (sortTransform?.kind === ArrayTransformKind.Shuffle) {
                    allSortColumns = sortColumns;
                    additionalProperty = b.object({
                        name: b.localizedString("random"),
                        [sortPropertyPropertyName]: b.constant(true),
                        ascIcon: b.constant(shuffleIconName),
                        descIcon: b.constant(shuffleIconName),
                    });
                    defaultProperty = true;
                    ascendingByDefault = true;
                } else if (sortTransform?.kind === ArrayTransformKind.Sort) {
                    allSortColumns = sortColumns;
                    ascendingByDefault = true;
                    const firstKey = first(sortTransform.keys);
                    if (firstKey !== undefined) {
                        const { key, order } = firstKey;
                        const column = definedMap(decomposeSortKey(key), c => getTableColumn(table, c));
                        if (column !== undefined) {
                            allSortColumns = [column, ...sortColumns.filter(c => c.name !== column.name)];
                        }
                        ascendingByDefault = order === SortOrder.Ascending;
                    }
                    defaultProperty = allSortColumns[0].name;
                } else {
                    allSortColumns = sortColumns;
                    additionalProperty = b.object({
                        name: b.localizedString("default"),
                        [sortPropertyPropertyName]: b.constant(false),
                        ascIcon: b.constant(ascIconName),
                        descIcon: b.constant(descIconName),
                    });
                    defaultProperty = false;
                    ascendingByDefault =
                        sortTransform?.kind === ArrayTransformKind.TableOrder ? !sortTransform.reverse : true;
                }

                const sortPropertiesItems = allSortColumns.map(c =>
                    b.object({
                        name: b.constant(getTableColumnDisplayName(c)),
                        [sortPropertyPropertyName]: b.hold(
                            convertForSort(b, c.type, b.getProperty(c.name, b.getContext, table))
                        ),
                        ascIcon: b.constant(ascIconName),
                        descIcon: b.constant(descIconName),
                    })
                );

                let sortPropertiesList: SerializedComponent;
                if (additionalProperty === undefined) {
                    sortPropertiesList = b.list(sortPropertiesItems);
                } else {
                    sortPropertiesList = b.list([additionalProperty, ...sortPropertiesItems]);
                }

                sortProperties = b.getVariable("sortProperties");
                sortProperty = b.getVariable(sortPropertyVariableName);
                sortAscending = b.getVariable("sortAscending");

                let sort = b.sort(
                    b.getVariable("toDynamicallySort"),
                    sortProperty,
                    b.if(sortAscending, b.constant("asc"), b.constant("desc"))
                );
                if (defaultProperty === false) {
                    sort = b.if(
                        b.areIdentical(sortProperty, b.constant(false)),
                        b.if(
                            sortAscending,
                            b.getVariable("toDynamicallySort"),
                            b.reverseList(b.getVariable("toDynamicallySort"))
                        ),
                        sort
                    );
                } else if (defaultProperty === true) {
                    sort = b.if(
                        b.areIdentical(sortProperty, b.constant(true)),
                        b.shuffle(b.getVariable("toDynamicallySort"), uniqueID),
                        sort
                    );
                }

                contextLets = [
                    ...contextLets,
                    ["sortProperties", sortPropertiesList],
                    [
                        sortPropertyVariableName,
                        b.clonePrimitive(
                            b.getNonTableProperty(
                                sortPropertyPropertyName,
                                b.listNth(b.getVariable("sortProperties"), 0)
                            )
                        ),
                    ],
                    ["sortAscending", b.data(ascendingByDefault)],
                    ["toDynamicallySort", arrayData],
                    ["dynamicallySorted", sort],
                ];

                arrayData = b.getVariable("dynamicallySorted");
            }

            let icon = b.icon("01-15-filter-1");
            if (filterValues !== undefined) {
                icon = b.ifIsNonEmptyArray(filterValues, b.icon("00-01-glide-filter-active"), icon);
            }

            menuItems.push({
                title: b.localizedString("filter"),
                icon,
                onTap: builder.makePushFilterScreen(
                    filterItems,
                    filterValues,
                    definedMap(dynamicFilterColumn, c => b.constant(getTableColumnDisplayName(c))),
                    sortProperties,
                    sortProperty,
                    sortAscending
                ),
                purpose: b.constant(MenuItemPurpose.FilterAndSearch),
            });
        }

        let maybeArrayScreenRow: SerializedComponent | undefined;
        if (this.hasComponents) {
            contextLets = [...contextLets, ["arrayScreenRow", arrayScreenRow]];
            maybeArrayScreenRow = b.getVariable("arrayScreenRow");
        }

        const arrayContent = this.makeContentForProcessedArray(
            builder,
            desc,
            table,
            b.getNonTableProperty("filtered", b.getContext),
            b.getNonTableProperty("searchActive", b.getContext),
            undefined,
            uniqueID,
            undefined,
            maybeArrayScreenRow !== undefined ? b.getNonTableProperty("arrayScreenRow", b.getContext) : undefined
        );
        const contentComponent = `array-content-${screenName}`;

        function makePivotContent(componentName: string, context: SerializedComponent): PivotContent {
            let content = b.lookupFunction(contentComponent);
            const contextObject: SerializedComponentsObject = { filtered: context };
            if (searchActiveFlag !== undefined) {
                contextObject.searchActive = searchActiveFlag;
            }
            if (maybeArrayScreenRow !== undefined) {
                contextObject.arrayScreenRow = maybeArrayScreenRow;
            }
            content = b.bindContextToValue(content, b.object(contextObject));
            return { componentName, component: content, menuItems };
        }

        let pivotPicker: SerializedComponent | undefined;
        let pivotComponents: PivotContent[];
        const getCurrentPivot = b.clonePrimitive(b.builtinCall("CurrentPivot", []), b.constant(0));
        if (pivots !== undefined) {
            pivotPicker = b.let(
                [["current", getCurrentPivot]],
                b.ui("PivotPicker", {
                    pivotNames: b.list(
                        pivots.map(p =>
                            p.title === undefined ? b.localizedString(p.titleLocalizedStringKey) : b.constant(p.title)
                        )
                    ),
                    current: b.getVariable("current"),
                    onChange: b.actionWithUserSpecificData(
                        b.action("SetCurrentPivot", { index: b.getVariable("current") }),
                        b.set(b.getVariable("current"), getCurrentPivot)
                    ),
                })
            );

            pivotComponents = pivots.map(({ filter }, i) => {
                let pivotData = arrayData;
                if (filter !== undefined) {
                    pivotData = makeFilteredArray(builder, filter, pivotData, table);
                }

                // FIXME: This is an ugly hack so always have a screen with the main
                // screen name because otherwise something goes wrong when we replace the
                // screen in the builder and I can't be bothered to figure it out now.
                const componentName = i === 0 ? screenName : `pivot-${i}-${screenName}`;
                return makePivotContent(componentName, pivotData);
            });
        } else {
            pivotComponents = [makePivotContent(screenName, arrayData)];
        }

        const flags = [ScreenFlag.IsArrayScreen];
        if (this.format === ArrayScreenFormat.Map) {
            flags.push(ScreenFlag.IsMapScreen);
        }

        return {
            pivotComponents,
            additionalComponents: makeSingletonMap(contentComponent, arrayContent),
            ctxModifier: b.captureLets(contextLets),
            title,
            topBar,
            pivotPicker,
            quotaBannerMessage: makeQuotaBannerMessage(b, this.quotaBannerRequirements),
            flags,
        };
    }

    public defaultDescription(
        tables: InputOutputTables,
        ccc: AppDescriptionContext,
        components: readonly ComponentDescription[]
    ): TScreenDesc | undefined {
        const type = makeTableRef(tables.input);
        const arrayDesc: ArrayScreenDescription = {
            kind: ScreenDescriptionKind.Array,
            type,
            format: this.format,
            fetchesData: false,
            canAddRow: makeSwitchProperty(false),
            canAddRowFilters: [],
            search: makeSwitchProperty(true),
            searchPlaceholder: makeStringProperty(""),
            reverse: makeSwitchProperty(false),
            groupByColumn: undefined,
            transforms: [],
            actions: [{ kind: ActionKind.PushDetailScreen }],
            components,
            dynamicFilterColumn: undefined,
            dynamicSortColumns: undefined,
            limit: undefined,
        };

        return populateDescription(
            d => this.getPropertyDescriptors(true, d, true, false, ccc),
            PopulationMode.Default,
            arrayDesc as TScreenDesc,
            arrayDesc as TScreenDesc,
            tables,
            ccc,
            false,
            undefined
        );
    }

    public getPropertyDescriptors(
        isTableRefArray: boolean,
        desc: TScreenDesc | undefined,
        isDefaultArrayScreen: boolean,
        withTransforms: boolean,
        ccc: AppDescriptionContext
    ): readonly PropertyDescriptor[] {
        const descriptors: PropertyDescriptor[] = [searchPropertyHandler];
        if (searchPropertyHandler.getSwitch(desc)) {
            descriptors.push(searchPlaceholderPropertyHandler);
        }

        if (isTableRefArray) {
            descriptors.push({
                kind: PropertyKind.Switch,
                property: { name: "canAddRow" },
                label: "Allow users to add items",
                section: PropertySection.Add,
            });

            descriptors.push(dynamicFilterColumnPropertyHandler);

            if (getCanAddRow(desc) && getFeatureSetting("addScreenConditions")) {
                descriptors.push({
                    kind: PropertyKind.Transforms,
                    property: { name: "canAddRowFilters" },
                    label: "Condition",
                    getIndirectTable: undefined,
                    addText: "Add condition",
                    description: "Set conditions for when adding is allowed.",
                    allowContextTable: false,
                    allowLHSUserProfileColumns: true,
                    withContainingScreen: false,
                    section: PropertySection.Add,
                });
            }

            if (this.getNeedsOrder(desc)) {
                descriptors.push(dynamicSortColumnsPropertyHandler);
            }

            descriptors.push(
                ...this.getContentPropertyDescriptors<TScreenDesc>(
                    undefined,
                    false,
                    desc,
                    ccc,
                    undefined,
                    isDefaultArrayScreen,
                    withTransforms,
                    undefined
                )
            );
        }

        return descriptors;
    }

    getPropertyAndActionDescriptors(
        isTableRefArray: boolean,
        desc: TScreenDesc,
        isDefaultArrayScreen: boolean,
        withTransforms: boolean,
        ccc: AppDescriptionContext
    ): readonly PropertyDescriptor[] {
        const props = this.getPropertyDescriptors(isTableRefArray, desc, isDefaultArrayScreen, withTransforms, ccc);
        const actions = this.getActionDescriptors(desc, undefined, ccc);
        return [...props, ...actions];
    }

    // FIXME: Do this via the descriptor, where we have the searchable flag.
    public abstract getBasicSearchProperties(desc: TContentDesc): readonly string[];

    private makeSearch(
        b: SerializedElementBuilder,
        builder: ComponentBuilder,
        desc: TScreenDesc,
        incomingArrayData: SerializedComponent,
        title: SerializedComponent
    ): MakeSearchResult {
        const arrayData = b.getVariable("array");
        const noSearchResult: MakeSearchResult = {
            contextLets: [
                ["array", incomingArrayData],
                ["title", title],
            ],
            arrayData,
            originalArray: arrayData,
            searchActiveFlag: b.constant(false),
        };

        const columnType = desc.type;

        if (!searchPropertyHandler.getSwitch(desc) || !isSingleRelationType(columnType)) {
            return noSearchResult;
        }

        let searchProperties = this.getBasicSearchProperties(desc);
        const searchVariables = makeSearchVariables(b);
        const searchedArray = makeSearchedArray(
            b,
            builder,
            incomingArrayData,
            searchProperties,
            columnType,
            searchVariables.trimmedSearchVariable
        );
        if (searchedArray === undefined) return noSearchResult;

        searchProperties = Array.from(new Set(searchProperties));
        let contextLets: LetBinding[] = [
            ["originalArray", incomingArrayData],
            ["array", searchedArray],
            ["title", title],
            ["searchActive", b.data(false)],
        ];
        const searchPlaceholderValue = withDefault(searchPlaceholderPropertyHandler.getString(desc), "").trim();
        const searchPlaceholderComponent =
            searchPlaceholderValue.length > 0 ? b.constant(searchPlaceholderValue) : undefined;
        const searchActiveFlag = b.getVariable("searchActive");
        const { topBar, searchLets } = makeSearchBar(b, searchActiveFlag, undefined, searchPlaceholderComponent, true);
        contextLets = [...searchLets, ...contextLets];

        const originalArray = b.getVariable("originalArray");

        return { contextLets, arrayData, originalArray, topBar, searchActiveFlag };
    }
}
